import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { Copy, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import AppSidebar from '@/components/AppSidebar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { apiService, type Interview } from '@/service/api'
import OverviewTab from './OverviewTab'
import InterviewGuideTab from './InterviewGuideTab'
import InterviewsTab from './InterviewsTab'
import DocumentsTab from './DocumentsTab'
import { PAGE_HEADER } from '@/components/layout/pageHeader'

type Tab = 'overview' | 'interview_guide' | 'interviews' | 'documents'

const HumanExpertTopicDetailsPage = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [interview, setInterview] = useState<Interview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    void (async () => {
      try {
        setLoading(true)
        const data = await apiService.getInterview(id)
        if (cancelled) return
        setInterview(data)
        setError(null)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load topic')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const shareUrl = id ? `${window.location.origin}/human-expert-knowledge/${id}` : ''

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success(t('hek_topic:share.copied', { defaultValue: 'Link copied to clipboard' }))
    } catch {
      toast.error(t('hek_topic:share.copy_failed', { defaultValue: 'Failed to copy link' }))
    }
  }

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Breadcrumb */}
        <header className={PAGE_HEADER}>
          <nav className="flex items-center gap-2 flex-1 text-sm">
            {/* UI-67: breadcrumb words only. The chevron stays — it is the
                separator, not decoration. */}
            <button
              type="button"
              onClick={() => navigate('/human-expert-knowledge')}
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
            >
              {t('nav:menu.expert_knowledge', { defaultValue: 'Knowledge Capture' })}
            </button>
            <ChevronRight className="size-3.5 text-muted-foreground" />
            <span className="inline-flex items-center font-medium text-foreground">
              {loading
                ? t('hek_topic:breadcrumb.loading', { defaultValue: 'Loading…' })
                : interview?.topic || id || '—'}
            </span>
          </nav>
        </header>

        {/* Tabs */}
        <div className="flex h-11 shrink-0 items-center gap-6 border-b border-border bg-background px-6">
          {([
            { id: 'overview', labelKey: 'hek_topic:tabs.overview', defaultLabel: 'Overview' },
            { id: 'interview_guide', labelKey: 'hek_topic:tabs.interview_guide', defaultLabel: 'Interview Guide' },
            { id: 'interviews', labelKey: 'hek_topic:tabs.interviews', defaultLabel: 'Interviews' },
            { id: 'documents', labelKey: 'hek_topic:tabs.documents', defaultLabel: 'Documents' },
          ] as const).map((tt) => {
            const active = tab === tt.id
            return (
              <button
                key={tt.id}
                type="button"
                onClick={() => setTab(tt.id)}
                className={`relative h-11 px-1 text-sm transition-colors ${
                  active
                    ? 'text-sky-600 font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(tt.labelKey, { defaultValue: tt.defaultLabel })}
                {active && (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 bg-sky-600" aria-hidden="true" />
                )}
              </button>
            )
          })}
        </div>

        {/* Content + right sidebar */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <main className="flex-1 min-w-0 overflow-auto px-6 py-6">
            {loading ? (
              <p className="text-sm text-muted-foreground">
                {t('hek_topic:loading', { defaultValue: 'Loading topic…' })}
              </p>
            ) : error ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {t('hek_topic:error.title', { defaultValue: 'Failed to load topic' })}
                </p>
                <p className="text-xs text-muted-foreground max-w-[480px]">{error}</p>
              </div>
            ) : !interview ? (
              <p className="text-sm text-muted-foreground">
                {t('hek_topic:not_found', { defaultValue: 'Topic not found.' })}
              </p>
            ) : tab === 'overview' ? (
              <OverviewTab
                interview={interview}
                onSwitchToDocuments={() => setTab('documents')}
              />
            ) : tab === 'interview_guide' ? (
              <InterviewGuideTab interview={interview} />
            ) : tab === 'interviews' ? (
              <InterviewsTab interview={interview} />
            ) : (
              <DocumentsTab interviewId={interview.id} />
            )}
          </main>

          {/* Right sidebar — Share + Invite */}
          <aside className="w-[320px] shrink-0 border-l border-border bg-background dark:bg-dark-card overflow-auto px-4 py-6 flex flex-col gap-4">
            <section className="rounded-lg border border-border p-4 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('hek_topic:share.title', { defaultValue: 'Share this topic' })}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('hek_topic:share.subtitle', {
                    defaultValue:
                      'Your new topic has been created. Invite colleagues to collaborate on this topic.',
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={shareUrl}
                  className="h-9 text-xs flex-1"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  onClick={copyShareUrl}
                  className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t('hek_topic:share.copy', { defaultValue: 'Copy link' })}
                >
                  <Copy className="size-4" />
                </button>
              </div>
            </section>
            <section className="rounded-lg border border-border p-4 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('hek_topic:invite.title', { defaultValue: 'Invite experts' })}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('hek_topic:invite.subtitle', {
                    defaultValue:
                      'Your new topic has been created. Invite colleagues to collaborate on this topic.',
                  })}
                </p>
              </div>
              <Button size="sm" className="w-full">
                {t('hek_topic:invite.cta', { defaultValue: 'Invite' })}
              </Button>
            </section>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default HumanExpertTopicDetailsPage
